import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Calendar as CalIcon, Crown, Edit3, Save, Settings2, Globe, Award } from "lucide-react";
import lessons from "../data/lessons.json";
import { authAPI, progressAPI } from "../api";
import { getStats, getLevel, getAllAchievementsWithStatus } from "../gamification";
import { isPremium } from "../components/PremiumGate";
import Avatar from "../components/atoms/Avatar";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

export default function Profile({ user, onUpdateUser }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [currentDay, setCurrentDay] = useState(Number(localStorage.getItem("currentDay") || 1));

  useEffect(() => {
    progressAPI.get().then((data) => {
      if (data.currentDay) {
        setCurrentDay(data.currentDay);
        localStorage.setItem("currentDay", String(data.currentDay));
      }
    }).catch(() => {});
  }, []);

  const stats = getStats();
  const levelInfo = getLevel(stats.totalXp);
  const achievements = getAllAchievementsWithStatus();
  const recentEarned = achievements.filter((a) => a.unlocked).slice(0, 3);
  const totalLessons = lessons.length;

  async function handleSave() {
    if (name.trim() && onUpdateUser) {
      try {
        const data = await authAPI.updateProfile({ name: name.trim() });
        onUpdateUser(data.user);
      } catch {
        onUpdateUser({ ...user, name: name.trim() });
      }
    }
    setEditing(false);
  }

  if (!user) {
    return (
      <div style={{ textAlign: "center", padding: "48px 16px" }}>
        <h2 className="font-display" style={{ fontSize: 36, fontWeight: 400 }}>Please sign in.</h2>
        <Link to="/login" style={{ color: "var(--gold-700)", borderBottom: "1px solid currentColor", textDecoration: "none", marginTop: 12, display: "inline-block" }}>
          Go to login →
        </Link>
      </div>
    );
  }

  const joinDate = user.joinedAt
    ? new Date(user.joinedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : "Recently";
  const initial = (user.name || "L")[0].toUpperCase();

  return (
    <div className="page-enter" style={{ paddingBottom: 48, display: "flex", flexDirection: "column", gap: 32 }}>
      {/* Head row */}
      <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap", paddingBottom: 32, borderBottom: "1px solid var(--border-1)" }}>
        <Avatar initials={initial} color="rust" size={120} />
        <div style={{ flex: 1, minWidth: 240 }}>
          {editing ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ fontFamily: "var(--font-display)", fontSize: 40, fontWeight: 400, padding: "4px 8px", border: "1px solid var(--gold-500)", borderRadius: "var(--radius-xs)", background: "#fff", outline: "none" }}
              />
              <Button variant="primary" size="sm" onClick={handleSave} icon={<Save size={14} strokeWidth={1.8} />}>Save</Button>
            </div>
          ) : (
            <h1 className="font-display" style={{ fontSize: 48, lineHeight: 1, fontWeight: 400, letterSpacing: "-0.02em", margin: "0 0 8px", display: "inline-flex", gap: 12, alignItems: "center" }}>
              {user.name}
              <button type="button" onClick={() => setEditing(true)} aria-label="Edit name"
                style={{ background: "transparent", border: 0, color: "var(--fg-3)", cursor: "pointer", padding: 6, borderRadius: 999 }}>
                <Edit3 size={16} strokeWidth={1.5} />
              </button>
            </h1>
          )}
          <div style={{ display: "flex", gap: 16, color: "var(--fg-3)", fontSize: 14, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><Mail size={14} strokeWidth={1.5} /> {user.email}</span>
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><CalIcon size={14} strokeWidth={1.5} /> Joined {joinDate}</span>
            {isPremium() && <span style={{ display: "inline-flex", gap: 6, alignItems: "center", color: "var(--gold-700)" }}><Crown size={14} strokeWidth={1.5} /> Premium</span>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/settings" style={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm" icon={<Settings2 size={14} strokeWidth={1.5} />}>Settings</Button>
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 }}>
        <Stat value={stats.totalXp}                label="XP earned" />
        <Stat value={`Lv ${levelInfo.level}`}      label={levelInfo.name} />
        <Stat value={stats.streak || 0}            label="Day streak" />
        <Stat value={currentDay - 1}               label="Days active" />
        <Stat value={stats.wordsLearned || 0}      label="Words mastered" />
        <Stat value={`${currentDay - 1}/${totalLessons}`} label="Lessons done" />
      </div>

      {/* Recent badges */}
      {recentEarned.length > 0 && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
            <h2 className="font-display" style={{ fontSize: 32, fontWeight: 400, margin: 0 }}>
              Recent <em style={{ color: "var(--gold-500)" }}>badges.</em>
            </h2>
            <Link to="/achievements" style={{ color: "var(--gold-700)", borderBottom: "1px solid currentColor", textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
              See all →
            </Link>
          </div>
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {recentEarned.map((a) => (
              <Card key={a.id} paperDeep>
                <div style={{ width: 48, height: 48, borderRadius: 999, background: "var(--paper)", border: "1px solid var(--gold-500)", color: "var(--gold-700)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
                  <Award size={22} strokeWidth={1.5} />
                </div>
                <h4 className="font-display" style={{ fontSize: 18, fontWeight: 400, margin: "0 0 4px" }}>{a.title}</h4>
                <p style={{ fontSize: 12, color: "var(--fg-3)", margin: 0 }}>{a.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Languages */}
      <div>
        <h2 className="font-display" style={{ fontSize: 32, fontWeight: 400, margin: "0 0 14px" }}>
          Languages I <em style={{ color: "var(--gold-500)" }}>speak.</em>
        </h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Chip>🇮🇳 Tamil <span style={{ opacity: 0.7 }}>· native</span></Chip>
          <Chip tone="gold"><Globe size={11} strokeWidth={1.8} /> English <span style={{ opacity: 0.7 }}>· learning</span></Chip>
        </div>
        <p style={{ fontSize: 13, color: "var(--fg-3)", marginTop: 8 }}>
          Flags are the one allowed pictographic exception in our system — they carry information emoji can't.
        </p>
      </div>

      {/* Premium CTA if not premium */}
      {!isPremium() && (
        <Card paperDeep style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div>
            <h3 className="font-display" style={{ fontSize: 26, fontWeight: 400, margin: "0 0 4px" }}>
              Premium <em style={{ color: "var(--gold-500)" }}>opens 38 more lessons.</em>
            </h3>
            <p style={{ color: "var(--fg-2)", fontSize: 14, margin: 0 }}>₹249/mo · cancel any time.</p>
          </div>
          <Link to="/pricing" style={{ textDecoration: "none" }}>
            <Button variant="primary" icon={<Crown size={14} strokeWidth={1.8} />}>See plans</Button>
          </Link>
        </Card>
      )}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border-2)", borderRadius: "var(--radius-md)", padding: 18 }}>
      <p className="font-display" style={{ fontSize: 36, lineHeight: 1, margin: 0, fontWeight: 400, color: "var(--ink-900)" }}>{value}</p>
      <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--fg-3)", margin: "6px 0 0" }}>{label}</p>
    </div>
  );
}
